import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

export const MIN_TIMELINE_ZOOM = 10;

// One ceiling for both places that zoom. They differ in where they START, not in how
// far they can go: the dock opens wide enough to see clips arranged against each other,
// the clip editor closer in on the single clip being cut — but either can be taken all
// the way to 100px/sec.
export const MAX_TIMELINE_ZOOM = 100;

// The dock opens more zoomed-out than the clip editor: its job is the arrangement, which
// needs several clips on screen at once.
export const DEFAULT_MAIN_TIMELINE_ZOOM = 35;
export const DEFAULT_TIMELINE_ZOOM = 50;

const MIN_CAPTION_FONT = 11;
const MAX_CAPTION_FONT = 16;

export const clampTimelineZoom = (value: number) => Math.min(MAX_TIMELINE_ZOOM, Math.max(MIN_TIMELINE_ZOOM, Math.round(value)));
export const captionFontForTimelineZoom = (zoom: number) => {
  const progress = (clampTimelineZoom(zoom) - MIN_TIMELINE_ZOOM) / (MAX_TIMELINE_ZOOM - MIN_TIMELINE_ZOOM);
  return MIN_CAPTION_FONT + progress * (MAX_CAPTION_FONT - MIN_CAPTION_FONT);
};
const zoomToSlider = (value: number) => (
  Math.log(clampTimelineZoom(value) / MIN_TIMELINE_ZOOM)
  / Math.log(MAX_TIMELINE_ZOOM / MIN_TIMELINE_ZOOM)
) * 100;
const sliderToZoom = (value: number) => clampTimelineZoom(
  MIN_TIMELINE_ZOOM * Math.pow(MAX_TIMELINE_ZOOM / MIN_TIMELINE_ZOOM, value / 100),
);

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
export const ZOOM_SHORTCUT_HINT = IS_MAC ? '⌘ + scroll' : 'Ctrl + scroll';

interface Props {
  zoom: number;
  onChange: (zoom: number) => void;
  label: string;
  controlName: string;
}

export default function TimelineZoomControls({ zoom, onChange, label, controlName }: Props) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end', px: 0.5, flexWrap: 'wrap' }}>
      <Tooltip title={`${label} (${ZOOM_SHORTCUT_HINT})`}>
        <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.5 }}>{label}</Typography>
      </Tooltip>
      <Tooltip title="Zoom out">
        <IconButton
          aria-label={`Zoom ${controlName} out`}
          size="small"
          onClick={() => onChange(clampTimelineZoom(zoom / 1.3))}
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Slider
        aria-label={label}
        value={zoomToSlider(zoom)}
        min={0}
        max={100}
        step={1}
        onChange={(_event, value) => onChange(sliderToZoom(value as number))}
        sx={{ width: 160 }}
      />
      <Tooltip title="Zoom in">
        <IconButton
          aria-label={`Zoom ${controlName} in`}
          size="small"
          onClick={() => onChange(clampTimelineZoom(zoom * 1.3))}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 68, fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(zoom)} px/sec
      </Typography>
    </Box>
  );
}
